import React from 'react';
import { useTranslation } from 'react-i18next';

import QueryFilter from '../../helpers/query-filter';

import { togglableFilters, toggleableExcludingFilters } from '../../shared/togglable-filters';

import { bindActionCreators } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeFilter,
  closeFoldout,
  hideFilterBar,
  openFilter,
  openFoldout,
  showFilterBar,
} from '../../actions/filters';
import { numberSeparator } from '../../helpers/formats';
import {
  type CombinedFacet,
  getExcludingFacetSelections,
  getFacetSelections,
  getQueryFilter,
} from '../../utils/reselectors';
import { TagGroup, TagItem } from '../tag';
import SaveSubscriptionButton from './save-subscription-button';

const HiddenParams = ['isAuctionItem', 'sellerType', 'leasing', 'businessLeasing', 'tags'];

const getRangeTitle = (facet: CombinedFacet) => {
  const preChar = facet.min === facet.range?.min ? facet.prefixChar : '';
  const sufChar = facet.max === facet.range?.max ? facet.suffixChar : '';
  const min = facet.valueFormat ? numberSeparator(facet.range?.min || 0) : facet.range?.min;
  const max = facet.valueFormat ? numberSeparator(facet.range?.max || 0) : facet.range?.max;

  return `${facet.prefix}${preChar}${min}–${max}${sufChar}${facet.suffix}`;
};

interface ActiveFiltersProps {
  umbrellaFacet?: string;
  onRequestSearch: (queryFilter: QueryFilter) => void;
}

const ActiveFilters = ({ umbrellaFacet, onRequestSearch }: ActiveFiltersProps) => {
  const { t } = useTranslation(['common', 'search']);
  const dispatch = useDispatch();

  const facets = useSelector(getFacetSelections);
  const excludingFacets = useSelector(getExcludingFacetSelections);
  const queryFilter = useSelector(getQueryFilter);

  const actions = bindActionCreators(
    { openFoldout, closeFoldout, openFilter, closeFilter, showFilterBar, hideFilterBar },
    dispatch,
  );

  const onDisplayFacet = (facet: CombinedFacet) =>
    actions.openFilter(facet, !process.env.SSR ? window.scrollY : 0, false);

  const onDisplayExcludingFacet = (facet: CombinedFacet) =>
    actions.openFilter(facet, !process.env.SSR ? window.scrollY : 0, true);

  const onRemoveFacet = (id: string) => {
    const vals = queryFilter.exclusion(id);
    const nextQueryFilter = queryFilter.remove(id);
    nextQueryFilter.setExclusion(id, vals);
    onRequestSearch(nextQueryFilter);
  };

  const onRemoveRange = (id: string) => {
    const nextQueryFilter = queryFilter.removeRange(id);
    onRequestSearch(nextQueryFilter);
  };

  const onRemoveExclusionFacet = (id: string) => {
    const vals = queryFilter.get(id);
    const nextQueryFilter = queryFilter.remove(id);
    nextQueryFilter.set(id, vals);
    onRequestSearch(nextQueryFilter);
  };

  const onRemoveFilter = (id: string, value: string) => {
    let nextQueryFilter = queryFilter.remove(id, value);

    if (
      !nextQueryFilter.removeAll(QueryFilter.OperationalParams.concat(HiddenParams)).hasFilters()
    ) {
      nextQueryFilter = nextQueryFilter.removeAll(
        QueryFilter.OperationalParams.concat(HiddenParams),
      );
    }

    onRequestSearch(nextQueryFilter);
  };

  const onRemoveSpatial = () => {
    const nextQueryFilter = queryFilter.removeSpatial();
    onRequestSearch(nextQueryFilter);
  };

  const onRemoveQuery = () => {
    const nextQueryFilter = queryFilter.removeQuery();
    onRequestSearch(nextQueryFilter);
  };

  const onClearFilters = () => {
    const nextQueryFilter = new QueryFilter();
    onRequestSearch(nextQueryFilter);
  };

  if (umbrellaFacet) {
    return (
      <div data-wx-actiongroupcontainer="" className="m-b">
        <div className="action-group-container-main">
          <div data-wx-filter="" aria-label={t('activeFiltersAriaLabel', { ns: 'search' })}>
            <div className="filter-list">
              {!queryFilter.isEmpty() && (
                <div className="filter-utilities">
                  <div className="filter is-clear">
                    <button
                      type="button"
                      className="filter-action"
                      title={t('Clear filter', { ns: 'search' })}
                      disabled={queryFilter.isEmpty()}
                      onClick={onClearFilters}
                      data-testid="clear-search-button"
                    >
                      <i className="icon-trash m-r-half" />
                      {t('Clear', { ns: 'common' })}
                    </button>
                  </div>
                </div>
              )}
              <div className="filter-items">
                <TagItem title={umbrellaFacet} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const modifiedQueryFilter = queryFilter.removeAll(
    QueryFilter.OperationalParams.concat(HiddenParams),
  );

  if (modifiedQueryFilter.isEmpty()) {
    return null;
  }

  let freetextFilter = null;
  const filters = togglableFilters().filter((filter) => modifiedQueryFilter.has(filter.id));
  const excludedFilters = toggleableExcludingFilters().filter((filter) =>
    modifiedQueryFilter.has(filter.id),
  );

  if (modifiedQueryFilter.hasQuery()) {
    freetextFilter = (
      <TagItem title={`"${modifiedQueryFilter.getQuery()}"`} onRemove={onRemoveQuery} />
    );
  }

  return (
    <div data-wx-actiongroupcontainer="" className="m-b">
      <SaveSubscriptionButton testId="save-search-button-desktop" />

      <div className="action-group-container-main">
        <div data-wx-filter="" aria-label={t('activeFiltersAriaLabel', { ns: 'search' })}>
          <div className="filter-list">
            {!queryFilter.isEmpty() && (
              <div className="filter-utilities">
                <div className="filter is-clear">
                  <button
                    type="button"
                    className="filter-action"
                    title={t('Clear filter', { ns: 'search' })}
                    disabled={queryFilter.isEmpty()}
                    onClick={onClearFilters}
                    data-testid="clear-search-button"
                  >
                    <i className="icon-trash m-r-half" />
                    {t('Clear', { ns: 'common' })}
                  </button>
                </div>
              </div>
            )}
            <div className="filter-items">
              {facets.map((facet) => {
                if (facet.isRangeFacet) {
                  return (
                    <TagItem
                      key={facet.id}
                      title={getRangeTitle(facet)}
                      onClick={() => onDisplayFacet(facet)}
                      onRemove={() => onRemoveRange(facet.id)}
                    />
                  );
                }
                if (facet.isLocationFacet) {
                  return (
                    <TagItem
                      key={facet.id}
                      title={facet.title || ''}
                      onClick={() => onDisplayFacet(facet)}
                      onRemove={() => onRemoveSpatial()}
                    />
                  );
                }

                if (facet.filters.length > 1) {
                  return (
                    <TagGroup
                      key={facet.id}
                      onRemove={() => onRemoveFacet(facet.id)}
                      title={facet.displayName}
                    >
                      {facet.filters
                        .sort((a, b) => a.displayName.localeCompare(b.displayName))
                        .map((filter) => (
                          <TagItem
                            key={filter.displayName}
                            title={filter.translatedDisplayName || filter.displayName}
                            onClick={() => onDisplayFacet(facet)}
                            onRemove={() => onRemoveFilter(facet.id, filter.displayName)}
                          />
                        ))}
                    </TagGroup>
                  );
                }

                return (
                  <TagItem
                    key={facet.id}
                    title={facet.filters[0].translatedDisplayName || facet.filters[0].displayName}
                    onClick={() => onDisplayFacet(facet)}
                    onRemove={() => onRemoveFilter(facet.id, facet.filters[0].displayName)}
                  />
                );
              })}
              {excludingFacets.map((excludingFacet) => {
                if (excludingFacet.filters.length > 1) {
                  return (
                    <TagGroup
                      key={excludingFacet.id}
                      onRemove={() => onRemoveExclusionFacet(excludingFacet.id)}
                      title={excludingFacet.displayName}
                    >
                      {excludingFacet.filters
                        .sort((a, b) => a.displayName.localeCompare(b.displayName))
                        .map((filter) => (
                          <TagItem
                            key={filter.displayName}
                            title={filter.translatedDisplayName || filter.displayName}
                            isExcluded={true}
                            onClick={() => onDisplayExcludingFacet(excludingFacet)}
                            onRemove={() => onRemoveFilter(excludingFacet.id, filter.displayName)}
                          />
                        ))}
                    </TagGroup>
                  );
                }

                return (
                  <TagItem
                    key={excludingFacet.id}
                    title={
                      excludingFacet.filters[0].translatedDisplayName ||
                      excludingFacet.filters[0].displayName
                    }
                    isExcluded={true}
                    onClick={() => onDisplayExcludingFacet(excludingFacet)}
                    onRemove={() =>
                      onRemoveFilter(excludingFacet.id, excludingFacet.filters[0].displayName)
                    }
                  />
                );
              })}
              {filters.map((filter) => (
                <TagItem
                  key={filter.id}
                  title={filter.displayName}
                  onRemove={() => onRemoveFacet(filter.id)}
                />
              ))}
              {excludedFilters.map((filter) => (
                <TagItem
                  key={filter.id}
                  title={filter.tagDisplayName}
                  isExcluded={true}
                  onRemove={() => onRemoveFacet(filter.id)}
                />
              ))}
              {freetextFilter}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveFilters;
